interface UserDetails {
  first_name: string
  last_name: string
  email: string
  phone?: string
  company?: string
  industry?: string
  sizeOfBusiness?: string
  profitabilityScore?: string
  capitalManagementScore?: string
  growthReadinessScore?: string
}

// Question mappings for better readability in PDF
const QUESTION_MAPPINGS: Record<string, string> = {
  profit_consistency: 'To what extent do your profits consistently convert into cashflow?',
  track_cashflow: 'How effectively do you track and manage cash flow separately from profit?',
  q_money_invested:
    'How well do you feel the money invested in your business is generating strong returns?',
  q_dep_outside_funding:
    'How dependent is your business on outside funding to maintain operations or grow?',
  q_futureGrowthAndExp:
    'How well is your business financially positioned to support growth and expansion?',
  sales: 'Annual Sales/Revenue',
  change_in_sales: 'Change in Sales (%)',
  net_operating_profit: 'Net Operating Profit',
  net_operating_profit_margin: 'Net Operating Profit Margin (%)',
  gross_margin: 'Gross Margin (%)',
  wage_exp_percentage: 'Wage Expense to Revenue (%)',
  wage_expense_to_revenue: 'Wage Expense to Revenue (%)',
  roa_percentage: 'Return on Assets (%)',
  roe_percentage: 'Return on Equity (%)',
}

// Scoring thresholds for converting numbers to Low/Medium/High
const SCORING_THRESHOLDS = {
  profitability: [12, 19, 25], // [low, medium, high]
  capitalManagement: [9, 15, 20], // [low, medium, high]
  growthReadiness: [4, 7, 10], // [low, medium, high]
}

// Business size mappings
const BUSINESS_SIZE_MAPPINGS: Record<string, string> = {
  micro: '1-4 employees (Micro)',
  small: '5-19 employees (Small)',
  medium: '20-199 employees (Medium)',
}

// Convert numeric score to descriptive text
const convertScoreToText = (
  scoreStr: string,
  category: keyof typeof SCORING_THRESHOLDS
): string => {
  const score = parseFloat(scoreStr)
  if (isNaN(score)) return 'N/A'

  // Safe access to thresholds using Map-based approach
  const thresholdMap = new Map([
    ['profitability', SCORING_THRESHOLDS.profitability],
    ['capitalManagement', SCORING_THRESHOLDS.capitalManagement],
    ['growthReadiness', SCORING_THRESHOLDS.growthReadiness],
  ])

  const thresholds = thresholdMap.get(category)
  if (!thresholds) return 'N/A'
  if (score <= thresholds[0]) return 'Low'
  if (score <= thresholds[1]) return 'Medium'
  return 'High'
}

// Convert business size code to readable format
const formatBusinessSize = (sizeValue: string): string => {
  // Handle formats like "micro:1-4 (Micro)" or just "micro"
  const sizeKey = sizeValue.includes(':') ? sizeValue.split(':')[0] : sizeValue
  return BUSINESS_SIZE_MAPPINGS[sizeKey.toLowerCase()] || sizeValue
}

interface StoryblokUploadResponse {
  success: boolean
  submissionId?: string
  asset?: {
    id: number
    filename: string
    public_url: string
    content_type: string
    content_length: number
    asset_folder_id: number | null
  }
  uploadedAt?: string
  error?: string
  existingSubmission?: {
    submissionId: string
    filename: string
    submittedAt: string
  }
}

// Enhanced PDF creation function specifically for Storyblok uploads
export const createEnhancedTextPDF = async (
  userDetails: UserDetails,
  formData: Record<string, string>
): Promise<string> => {
  // Import jsPDF dynamically to avoid SSR issues
  const { default: jsPDF } = await import('jspdf')

  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'mm',
    format: 'a4',
    compress: true, // Enable compression
  })
  const pageWidth = 210
  const margin = 15 // Reduced margin for more content
  const maxWidth = pageWidth - margin * 2
  let currentY = margin

  // Helper function to add text with line wrapping - optimized for size
  const addText = (text: string, fontSize: number = 10, style: 'normal' | 'bold' = 'normal') => {
    pdf.setFontSize(fontSize)
    pdf.setFont('helvetica', style)

    const lines = pdf.splitTextToSize(text, maxWidth)
    for (const line of lines) {
      if (currentY > 285 - margin) {
        // More space per page
        pdf.addPage()
        currentY = margin
      }
      pdf.text(line, margin, currentY)
      currentY += fontSize * 0.3 + 1 // Tighter line height
    }
    currentY += 3 // Reduced spacing after text block
  }

  // Title - smaller font
  addText('Business Rating Calculator - Submission Details', 14, 'bold')

  // Submission date - smaller font
  addText(`Submitted: ${new Date().toLocaleString('en-AU')}`, 8)
  currentY += 3

  // Contact Information - smaller font
  addText('Contact Information:', 12, 'bold')
  addText(`Name: ${userDetails.first_name} ${userDetails.last_name}`)
  addText(`Email: ${userDetails.email}`)
  if (userDetails.phone !== null && userDetails.phone !== undefined && userDetails.phone.length > 0)
    addText(`Phone: ${userDetails.phone}`)
  if (
    userDetails.company !== null &&
    userDetails.company !== undefined &&
    userDetails.company.length > 0
  )
    addText(`Company: ${userDetails.company}`)
  if (
    userDetails.industry !== null &&
    userDetails.industry !== undefined &&
    userDetails.industry.length > 0
  )
    addText(`Industry: ${userDetails.industry}`)
  if (
    userDetails.sizeOfBusiness !== null &&
    userDetails.sizeOfBusiness !== undefined &&
    userDetails.sizeOfBusiness.length > 0
  ) {
    const formattedSize = formatBusinessSize(userDetails.sizeOfBusiness)
    addText(`Business Size: ${formattedSize}`)
  }

  // Business Rating Scores - enhanced format with Low/Medium/High
  const hasScores = Boolean(
    (userDetails.profitabilityScore !== null &&
      userDetails.profitabilityScore !== undefined &&
      userDetails.profitabilityScore.length > 0) ||
    (userDetails.capitalManagementScore !== null &&
      userDetails.capitalManagementScore !== undefined &&
      userDetails.capitalManagementScore.length > 0) ||
    (userDetails.growthReadinessScore !== null &&
      userDetails.growthReadinessScore !== undefined &&
      userDetails.growthReadinessScore.length > 0)
  )
  if (hasScores) {
    addText('Business Rating Scores:', 12, 'bold')
    if (
      userDetails.profitabilityScore !== null &&
      userDetails.profitabilityScore !== undefined &&
      userDetails.profitabilityScore.length > 0
    ) {
      const scoreText = convertScoreToText(userDetails.profitabilityScore, 'profitability')
      addText(
        `Profitability and Cash Flow Alignment: ${scoreText} (${userDetails.profitabilityScore})`
      )
    }
    if (
      userDetails.capitalManagementScore !== null &&
      userDetails.capitalManagementScore !== undefined &&
      userDetails.capitalManagementScore.length > 0
    ) {
      const scoreText = convertScoreToText(userDetails.capitalManagementScore, 'capitalManagement')
      addText(`Capital Management: ${scoreText} (${userDetails.capitalManagementScore})`)
    }
    if (
      userDetails.growthReadinessScore !== null &&
      userDetails.growthReadinessScore !== undefined &&
      userDetails.growthReadinessScore.length > 0
    ) {
      const scoreText = convertScoreToText(userDetails.growthReadinessScore, 'growthReadiness')
      addText(`Growth Readiness: ${scoreText} (${userDetails.growthReadinessScore})`)
    }
  }

  // Financial Information and Survey Responses - enhanced format
  const financialData = Object.entries(formData)
    .filter(([, value]) => value !== null && value !== undefined && value.trim() !== '')
    .filter(([key]) => {
      // Filter out personal info that's already shown above
      const skipFields = [
        'first_name',
        'last_name',
        'email',
        'phone',
        'company',
        'industrySelect',
        'sizeOfBusiness',
      ]
      return !skipFields.includes(key)
    })
    .map(([key, value]) => {
      // Use custom question mapping if available, otherwise format the key
      // Safe access to question mappings using Map-based approach
      const questionMap = new Map(Object.entries(QUESTION_MAPPINGS))
      const question =
        questionMap.get(key) ?? key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())

      // For slider questions (q_*), convert numeric values to more readable format
      if (key.startsWith('q_') && !isNaN(parseFloat(value))) {
        const numValue = parseFloat(value)
        let description = ''
        if (numValue <= 2) description = ' (Low)'
        else if (numValue <= 4) description = ' (Medium)'
        else description = ' (High)'
        return `${question}: ${value}${description}`
      }

      return `${question}: ${value}`
    })

  if (financialData.length > 0) {
    addText('Survey Responses and Financial Information:', 12, 'bold')
    for (const item of financialData) {
      addText(`• ${item}`)
    }
  }

  // Important Notice
  addText('Important Notice:', 12, 'bold')
  addText(
    'This report has been generated based on the information provided. The ratings and analysis are for informational purposes only and should not be considered as financial advice. Please consult with a qualified financial advisor for personalized recommendations.'
  )

  // Return base64 string
  return pdf.output('datauristring').split(',')[1]
}

// Upload PDF with form data to Storyblok assets (with abuse prevention)
// Helper function to check and warn about PDF size
const checkPdfSize = (
  base64Pdf: string
): { sizeBytes: number; sizeMB: number; withinLimit: boolean } => {
  const sizeBytes = Math.ceil((base64Pdf.length * 3) / 4)
  const sizeMB = sizeBytes / (1024 * 1024)
  const maxSizeBytes = 4 * 1024 * 1024 // 4MB (Vercel has 4.5MB payload limit)

  return {
    sizeBytes,
    sizeMB,
    withinLimit: sizeBytes <= maxSizeBytes,
  }
}

export const uploadPDFToStoryblok = async (
  userDetails: UserDetails,
  formData: Record<string, string>,
  reportPdfBlob?: Blob,
  options?: {
    assetFolderId?: number
    filename?: string
    submissionId?: string
  }
): Promise<StoryblokUploadResponse> => {
  try {
    // Check if we should use the large file upload path
    if (reportPdfBlob) {
      const reportSizeMB = reportPdfBlob.size / (1024 * 1024)
      const directUploadThresholdMB = 3 // Use blob upload for files > 3MB

      // For large files, use Vercel Blob + background processing
      if (reportSizeMB > directUploadThresholdMB) {
        return await uploadLargePDFViaBlob(userDetails, formData, reportPdfBlob, options)
      }
    }

    // Create a combined PDF with form data + report PDF
    let combinedPdfBase64: string
    if (reportPdfBlob) {
      // Use pdf-lib to properly merge PDFs
      const { PDFDocument } = await import('pdf-lib')

      // Create enhanced form data PDF first
      const formDataPdfBase64 = await createEnhancedTextPDF(userDetails, formData)
      const formDataPdfBytes = Uint8Array.from(atob(formDataPdfBase64), (c) => c.charCodeAt(0))

      // Get the report PDF bytes and pre-compress it
      const reportPdfBytes = new Uint8Array(await reportPdfBlob.arrayBuffer())

      // Pre-compress the report PDF to reduce size before merging
      const tempReportPdf = await PDFDocument.load(reportPdfBytes)
      const compressedReportPdfBytes = await tempReportPdf.save({
        useObjectStreams: true,
        objectsPerTick: 25,
        updateFieldAppearances: false,
      })

      // Create merged PDF document
      const mergedPdf = await PDFDocument.create()

      // Load form data PDF and copy its pages
      const formDataPdf = await PDFDocument.load(formDataPdfBytes)
      const formDataPages = await mergedPdf.copyPages(formDataPdf, formDataPdf.getPageIndices())
      formDataPages.forEach((page) => mergedPdf.addPage(page))

      // Load the pre-compressed report PDF
      const reportPdf = await PDFDocument.load(compressedReportPdfBytes)

      // Apply compression to each page in the report PDF
      const reportPages = await mergedPdf.copyPages(reportPdf, reportPdf.getPageIndices())
      reportPages.forEach((page) => {
        // Scale down the page if it's too large (compress content)
        const { width, height } = page.getSize()
        if (width > 595 || height > 842) {
          // A4 size limits
          const scale = Math.min(595 / width, 842 / height)
          page.scaleContent(scale, scale)
          page.setSize(width * scale, height * scale)
        }
        mergedPdf.addPage(page)
      })

      // Generate final merged PDF with maximum compression
      const mergedPdfBytes = await mergedPdf.save({
        useObjectStreams: true, // Use object streams for better compression
        addDefaultPage: false,
        objectsPerTick: 25, // Smaller chunks for better compression
        updateFieldAppearances: false, // Skip field appearance updates
      })

      // Convert to base64 using chunked approach to avoid "too many arguments"
      let binaryString = ''
      const chunkSize = 8192
      for (let i = 0; i < mergedPdfBytes.length; i += chunkSize) {
        const chunk = mergedPdfBytes.slice(i, i + chunkSize)
        binaryString += String.fromCharCode.apply(null, Array.from(chunk))
      }
      combinedPdfBase64 = btoa(binaryString)
    } else {
      // If no report PDF, just create enhanced form data PDF for upload
      combinedPdfBase64 = await createEnhancedTextPDF(userDetails, formData)
    }

    const pdfBase64 = combinedPdfBase64

    // Check file size before upload
    let sizeInfo = checkPdfSize(pdfBase64)

    // If still too large with report PDF, try form data only as fallback
    if (!sizeInfo.withinLimit && reportPdfBlob) {
      combinedPdfBase64 = await createEnhancedTextPDF(userDetails, formData)
      sizeInfo = checkPdfSize(combinedPdfBase64)
    }

    if (!sizeInfo.withinLimit) {
      return {
        success: false,
        error: `PDF file is too large (${sizeInfo.sizeMB.toFixed(2)}MB). Maximum allowed size is 4MB due to Vercel's function limits. Please try reducing the number of pages or image quality in the report.`,
      }
    }

    // Convert base64 to blob for multipart upload
    const pdfBytes = Uint8Array.from(atob(combinedPdfBase64), (c) => c.charCodeAt(0))
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' })

    // Use multipart form data to bypass JSON size limits
    const formDataToSend = new FormData()
    formDataToSend.append('pdfFile', pdfBlob, 'report.pdf')
    formDataToSend.append('userDetails', JSON.stringify(userDetails))
    formDataToSend.append('formData', JSON.stringify(formData))

    if (options?.assetFolderId !== null && options?.assetFolderId !== undefined) {
      formDataToSend.append('assetFolderId', options.assetFolderId.toString())
    }
    if (options?.filename !== null && options?.filename !== undefined) {
      formDataToSend.append('filename', options.filename)
    }
    if (options?.submissionId !== null && options?.submissionId !== undefined) {
      formDataToSend.append('submissionId', options.submissionId)
    }

    const response = await fetch('/api/upload-to-storyblok', {
      method: 'POST',
      // Don't set Content-Type header - let browser set it with boundary
      body: formDataToSend,
    })

    // Check if response is ok first
    if (!response.ok) {
      let errorMessage = `Upload failed with status ${response.status}`

      // Handle specific status codes
      if (response.status === 413) {
        return {
          success: false,
          error: `PDF file is too large (${sizeInfo.sizeMB.toFixed(2)}MB). The server rejected the upload. Maximum allowed size is 4MB due to Vercel's function limits.`,
        }
      }

      try {
        // Try to parse as JSON for structured error
        const result = await response.json()
        // Handle duplicate submission specifically
        if (response.status === 409) {
          return {
            success: false,
            error: result.error as string,
            existingSubmission: result.existingSubmission,
          }
        }

        errorMessage = (result.error as string) ?? errorMessage
      } catch {
        // If JSON parsing fails, try to get text response
        try {
          const textResponse = await response.text()
          if (textResponse && textResponse.length > 0) {
            errorMessage = `Upload failed: ${textResponse.substring(0, 200)}...`
          }
        } catch {
          // Fallback to status-based error message
          errorMessage = `Upload failed with status ${response.status}: ${response.statusText}`
        }
      }

      throw new Error(errorMessage)
    }

    // Parse successful response
    let result
    try {
      result = await response.json()
    } catch {
      throw new Error('Server returned invalid JSON response')
    }

    return {
      success: true,
      submissionId: result.submissionId,
      asset: result.asset,
      uploadedAt: result.uploadedAt,
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to upload PDF to Storyblok',
    }
  }
}

// Generate and download PDF locally (maintains existing functionality)
export const generateAndDownloadPDF = async (
  userDetails: UserDetails,
  reportPdfBlob: Blob,
  filename?: string
): Promise<void> => {
  // This function maintains the existing PDF download functionality
  // It doesn't upload to Storyblok, just downloads the PDF as before

  try {
    const url = URL.createObjectURL(reportPdfBlob)
    const link = document.createElement('a')
    link.href = url
    const defaultFilename = `business-rating-${userDetails.first_name.toLowerCase()}-${userDetails.last_name.toLowerCase()}.pdf`
    link.download =
      filename !== null && filename !== undefined && filename.length > 0
        ? filename
        : defaultFilename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  } catch {
    throw new Error('Failed to download PDF')
  }
}

export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  let binary = ''
  const bytes = new Uint8Array(buffer)
  const len = bytes.byteLength
  for (let i = 0; i < len; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary)
}

// Upload large PDFs using sequential chunk uploads
const uploadLargePDFViaBlob = async (
  userDetails: UserDetails,
  formData: Record<string, string>,
  reportPdfBlob: Blob,
  options?: {
    assetFolderId?: number
    filename?: string
    submissionId?: string
  }
): Promise<StoryblokUploadResponse> => {
  console.warn(
    `Starting large PDF upload: ${Math.round((reportPdfBlob.size / 1024 / 1024) * 100) / 100}MB`
  )

  let mergedPdfBlob: Blob | null = null

  try {
    // Generate unique session ID
    const sessionId = crypto.randomUUID()

    // Merge formData + report PDF
    mergedPdfBlob = reportPdfBlob
    // Convert merged PDF into base64 chunks for upload
    const chunks = await convertBlobToBase64Chunks(mergedPdfBlob!)

    // Prepare metadata for the last chunk
    const metadata = {
      userDetails,
      formData,
      submissionId: options?.submissionId,
      assetFolderId: options?.assetFolderId,
      filename: options?.filename,
    }

    let finalResult: {
      success: boolean
      submissionId: string
      asset: {
        id: number
        filename: string
        public_url: string
        content_type: string
        content_length: number
        asset_folder_id: number | null
      }
      uploadedAt: string
    } | null = null

    // Upload chunks sequentially
    for (let i = 0; i < chunks.length; i++) {
      const isLastChunk = i === chunks.length - 1

      const requestBody = {
        sessionId,
        chunkIndex: i,
        chunk: chunks[i],
        totalChunks: chunks.length,
        isLastChunk,
        metadata: isLastChunk ? metadata : undefined,
      }

      const chunkResponse = await fetch('/api/upload-pdf-chunk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      })

      if (!chunkResponse.ok) {
        const errorText = await chunkResponse.text()
        console.error(`Chunk ${i + 1} upload failed:`, chunkResponse.status, errorText)
        throw new Error(`Chunk upload failed: ${chunkResponse.status} ${errorText}`)
      }

      let chunkResult
      try {
        chunkResult = await chunkResponse.json()
      } catch (parseError) {
        console.error('Failed to parse chunk response JSON:', parseError)
        throw new Error('Failed to parse chunk response JSON')
      }

      if (isLastChunk) {
        if (chunkResult.success === true && chunkResult.submissionId) {
          finalResult = {
            success: true,
            submissionId: chunkResult.submissionId,
            asset: chunkResult.asset,
            uploadedAt: chunkResult.uploadedAt,
          }
          break
        } else {
          console.error('Upload completed but missing submissionId:', chunkResult.submissionId)
          throw new Error(
            `Upload completed but server response is missing submissionId. Response success: ${chunkResult.success}, submissionId: ${chunkResult.submissionId}, error: ${chunkResult.error ?? 'none'}`
          )
        }
      }
    }

    if (!finalResult) throw new Error('Upload completed but no final result received')

    console.warn('Large PDF upload completed successfully with new token')
    return finalResult
  } catch (error) {
    console.error(
      'Error in large PDF upload:',
      error instanceof Error ? error.message : String(error)
    )

    // Fallback: download merged PDF for testing
    // if (mergedPdfBlob) {
    //   try {
    //     const url = URL.createObjectURL(mergedPdfBlob)
    //     const link = document.createElement('a')
    //     link.href = url
    //     link.download = options?.filename || 'report.pdf'
    //     document.body.appendChild(link)
    //     link.click()
    //     link.remove()
    //     URL.revokeObjectURL(url)
    //     console.warn('Fallback download triggered because upload failed.')
    //   } catch (downloadError) {
    //     console.error('Failed to trigger fallback download:', downloadError)
    //   }
    // }

    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'Failed to upload large PDF via sequential chunks',
    }
  }
}

// Helper function to convert blob to base64 chunks
const convertBlobToBase64Chunks = async (blob: Blob): Promise<string[]> => {
  const chunkSize = 1024 * 1024 // 1MB chunks
  const chunks: string[] = []
  const arrayBuffer = await blob.arrayBuffer()
  const uint8Array = new Uint8Array(arrayBuffer)

  for (let i = 0; i < uint8Array.length; i += chunkSize) {
    const chunk = uint8Array.slice(i, i + chunkSize)
    let binaryString = ''
    for (let j = 0; j < chunk.length; j++) {
      binaryString += String.fromCharCode(chunk[j]) // eslint-disable-line security/detect-object-injection
    }
    chunks.push(btoa(binaryString))
  }

  return chunks
}

// Generate PDF with form data only (no report)
export const generateFormDataPDF = async (
  userDetails: UserDetails,
  formData: Record<string, string>,
  options?: {
    includeLetterhead?: boolean
    includeFooter?: boolean
    download?: boolean
    filename?: string
  }
): Promise<{ base64: string; blob: Blob }> => {
  try {
    const { createCompletePDF } = await import('./pdfGenerator')

    const { base64, arrayBuffer } = await createCompletePDF(
      userDetails,
      formData,
      undefined, // No report PDF
      {
        includeLetterhead: options?.includeLetterhead ?? true,
        includeFooter: options?.includeFooter ?? true,
      }
    )

    const blob = new Blob([arrayBuffer], { type: 'application/pdf' })

    // Auto-download if requested
    if (options?.download === true) {
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      const defaultFilename = `business-form-${userDetails.first_name.toLowerCase()}-${userDetails.last_name.toLowerCase()}.pdf`
      link.download =
        options.filename !== null && options.filename !== undefined && options.filename.length > 0
          ? options.filename
          : defaultFilename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }

    return { base64, blob }
  } catch {
    throw new Error('Failed to generate PDF with form data')
  }
}
